import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppWindow, Briefcase, CheckSquare, Megaphone, type LucideIcon } from 'lucide-react';
import { AppHubLauncher, type AppLauncherItem } from '@miror/ui-web';
import {
  useSsoLaunch,
  useAppUrlOverrides,
  type MyAppItem,
  type SsoCreateLinkResponse,
} from '@miror/auth-sdk-web/react';
import { coreAuthed } from '../../services/coreAuthed';
import { CORE_API_BASE_URL } from '../../constants/index';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../lib/toast';

const CURRENT_APP_KEY = 'miror_task';

const ICON_MAP: Record<string, LucideIcon> = {
  briefcase: Briefcase,
  'check-square': CheckSquare,
  megaphone: Megaphone,
};

// O Core devolve logo_url relativo (/v1/media/{id}). Resolve pra absoluto usando a
// origem do Core (CORE_API_BASE_URL sem o /v1). Assim o launcher mostra o logo real
// de cada app em vez do ícone genérico.
const CORE_ORIGIN = CORE_API_BASE_URL.replace(/\/v1\/?$/, '');
function resolveLogoUrl(u?: string | null): string | null {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  return `${CORE_ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`;
}

// O /me/apps inclui logo_url (não tipado no MyAppItem do auth-sdk).
type MyApp = MyAppItem & { logo_url?: string | null };

/**
 * Launcher "Apps da Suíte" — mesma solução do Miror Pro (AppHubLauncher do
 * @miror/ui-web + useSsoLaunch do @miror/auth-sdk-web). Difere do Pro só na
 * fonte de dados: busca /me/apps direto no Core (sem react-query/BFF) e cria o
 * link SSO via POST /sso/create-link no Core.
 */
export function AppHub({ className }: { className?: string }) {
  const { companyId, user } = useAuth();
  const [apps, setApps] = useState<MyApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await coreAuthed.get<MyApp[]>('/me/apps');
      setApps(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const overrides = useAppUrlOverrides();
  const ssoLaunch = useSsoLaunch({
    activeCompanyId: companyId ?? null,
    createSsoLink: async (body, signal) => {
      const res = await coreAuthed.post<SsoCreateLinkResponse>('/sso/create-link', body, { signal });
      return res.data;
    },
    onSsoFailure: () => {
      toast.error('Login automático indisponível — faça login no app destino.');
    },
  });

  const items = useMemo<AppLauncherItem[]>(() => {
    const mapped: AppLauncherItem[] = apps.map((app) => {
      const Icon = ICON_MAP[app.icon] ?? AppWindow;
      return {
        key: app.key,
        name: app.name,
        baseUrl: overrides.resolveUrl(app.key, app.base_url),
        launchMethod: app.launch_method,
        icon: <Icon className="h-5 w-5" />,
        logoUrl: resolveLogoUrl(app.logo_url),
        companyName: app.company_name,
        isCurrent: app.key === CURRENT_APP_KEY,
        comingSoon: app.coming_soon ?? false,
      };
    });

    // Garante o card do app atual mesmo que /me/apps não o devolva.
    if (!mapped.some((i) => i.isCurrent)) {
      mapped.unshift({
        key: CURRENT_APP_KEY,
        name: 'Glivo Task',
        baseUrl: window.location.origin,
        launchMethod: 'url',
        icon: <CheckSquare className="h-5 w-5" />,
        isCurrent: true,
      });
    }
    return mapped;
  }, [apps, overrides]);

  return (
    <AppHubLauncher
      items={items}
      isLoading={loading}
      error={error}
      onRetry={() => void load()}
      onLaunch={(item) => {
        if (item.isCurrent || item.comingSoon) return;
        void ssoLaunch.launch(item);
      }}
      launchingKey={ssoLaunch.pendingKey}
      isSuperAdmin={user?.is_super_admin ?? false}
      urlOverrides={overrides.overrides}
      onSetOverride={overrides.setOverride}
      onClearOverride={overrides.clearOverride}
      onClearAll={overrides.clearAll}
      triggerClassName={className}
    />
  );
}
